import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Bus, Loader2, AlertCircle } from 'lucide-react';
import { getSriLankaDateString, getMinSelectableDate, getMaxSelectableDate } from '../utils/dateUtils';
import { scheduleApi } from '../services/api';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

const Schedules = () => {
  const [selectedDate, setSelectedDate] = useState(getSriLankaDateString());
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load schedules for selected date
  const loadSchedules = async () => {
    if (!selectedDate) {
      return;
    }

    // Validate that selected date is not in the past
    const today = getMinSelectableDate();
    if (selectedDate < today) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Date',
          text: 'Travel date cannot be in the past. Please select today\'s date or a future date.',
          confirmButtonColor: '#dc2626'
        });
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Get all active schedules
      const response = await scheduleApi.getSchedules();
      
      let schedulesArray = [];
      if (response?.value && Array.isArray(response.value)) {
        schedulesArray = response.value;
      } else if (Array.isArray(response)) {
        schedulesArray = response;
      }

      // Filter schedules that have schedule dates for selected date and find matching date
      const targetDate = new Date(selectedDate + 'T00:00:00.000Z');
      const filteredSchedules = schedulesArray
        .filter(schedule => 
          schedule.scheduleDates && 
          schedule.scheduleDates.length > 0 &&
          schedule.scheduleDates.some(sd => {
            const scheduleDate = new Date(sd.date);
            return scheduleDate.toDateString() === targetDate.toDateString();
          })
        )
        .map(schedule => {
          // Find the matching scheduleDate for the selected date
          const matchingScheduleDate = schedule.scheduleDates.find(sd => {
            const scheduleDate = new Date(sd.date);
            return scheduleDate.toDateString() === targetDate.toDateString();
          });
          
          // Return schedule with matching scheduleDate stored
          return {
            ...schedule,
            matchingScheduleDate: matchingScheduleDate || schedule.scheduleDates[0] // Fallback to first if not found
          };
        });

      setSchedules(filteredSchedules);
    } catch (err) {
      console.error('Error loading schedules:', err);
      setError('Failed to load schedules. Please try again.');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  // Load schedules when date changes
  useEffect(() => {
    loadSchedules();
  }, [selectedDate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800';
      case 'Limited':
        return 'bg-yellow-100 text-yellow-800';
      case 'Full':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeatAvailabilityColor = (available, total) => {
    const percentage = (available / total) * 100;
    if (percentage > 60) return 'text-green-600';
    if (percentage > 30) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatPrice = (price) => {
    return `Rs ${parseFloat(price).toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">Bus Schedules</h1>
            <p className="text-xs sm:text-sm text-blue-100">
            Find the perfect schedule for your journey
          </p>
          </div>
        </div>
      </section>

      {/* Date Selection Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Travel Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    const selectedDateValue = e.target.value;
                    const today = getMinSelectableDate();
                    if (selectedDateValue >= today) {
                      setSelectedDate(selectedDateValue);
                    }
                  }}
                  min={getMinSelectableDate()}
                  max={getMaxSelectableDate()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>
          </div>
        </div>
      </section>

      {/* Schedules Section */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Available Schedules
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {loading ? 'Loading schedules...' : `Showing ${schedules.length} schedule${schedules.length !== 1 ? 's' : ''} for ${new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
              </p>
            </div>
            
            {error && (
              <div className="px-6 py-4 bg-red-50 border-b border-red-200">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            {loading ? (
              <div className="px-6 py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
                <p className="text-gray-600">Loading schedules...</p>
              </div>
            ) : schedules.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Bus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedules Found</h3>
                <p className="text-gray-600">
                  No schedules available for the selected date. Please try selecting a different date.
                </p>
              </div>
            ) : (
            <div className="divide-y divide-gray-200">
              {schedules.map((schedule) => (
                  <div key={schedule.scheduleID} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-center">
                    {/* Route & Bus Info */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Bus className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {schedule.route ? `${schedule.route.fromLocation} → ${schedule.route.toLocation}` : 'Route not available'}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Bus className="w-4 h-4 mr-1" />
                                 {schedule.vehicle ? schedule.vehicle.registrationNumber : 'N/A'}
                            </span>
                            <span>•</span>
                               <span>{schedule.vehicle ? `${schedule.vehicle.brand} ${schedule.vehicle.model}` : 'N/A'}</span>
                               {schedule.vehicle && schedule.vehicle.busType && (
                                 <>
                                   <span>•</span>
                                   <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                     {schedule.vehicle.busType}
                                   </span>
                                 </>
                               )}
                          </div>
                            <p className="text-sm text-gray-500 mt-1">{schedule.scheduleName}</p>
                        </div>
                      </div>
                    </div>

                    {/* Time & Duration */}
                    <div className="text-center">
                      {schedule.matchingScheduleDate && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Departure</p>
                            <p className="font-semibold text-gray-900">{formatTime(schedule.matchingScheduleDate.departureTime)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500 mb-1">Arrival</p>
                            <p className="font-semibold text-gray-900">{formatTime(schedule.matchingScheduleDate.arrivalTime)}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Price & Availability */}
                    <div className="text-center">
                      <div className="mb-3">
                          <p className="text-2xl font-bold text-blue-600">
                            {(() => {
                              // Use route-based pricing: basePrice + serviceCharge (same logic as dashboard)
                              const route = schedule.route;
                              if (route) {
                                const basePrice = route.basePrice || 0;
                                const serviceCharge = route.serviceCharge || 0;
                                const totalPrice = basePrice + serviceCharge;
                                return formatPrice(totalPrice);
                              }
                              // Fallback to schedule date price if route is not available
                              return formatPrice(schedule.matchingScheduleDate?.price || 0);
                            })()}
                          </p>
                      </div>
                      
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-500">Seats</span>
                            <span className={`font-medium ${getSeatAvailabilityColor(schedule.matchingScheduleDate?.availableSeats || 0, schedule.vehicle?.totalSeats || 0)}`}>
                              {schedule.matchingScheduleDate?.availableSeats || 0}/{schedule.vehicle?.totalSeats || 0}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                                ((schedule.matchingScheduleDate?.availableSeats || 0) / (schedule.vehicle?.totalSeats || 1)) > 0.6 
                                ? 'bg-green-500' 
                                  : ((schedule.matchingScheduleDate?.availableSeats || 0) / (schedule.vehicle?.totalSeats || 1)) > 0.3 
                                ? 'bg-yellow-500' 
                                : 'bg-red-500'
                            }`}
                              style={{ width: `${((schedule.matchingScheduleDate?.availableSeats || 0) / (schedule.vehicle?.totalSeats || 1)) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-center space-x-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${schedule.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {schedule.isActive ? 'Available' : 'Inactive'}
                        </span>
                          <Link 
                            to={{
                              pathname: "/book",
                              search: `?routeId=${schedule.routeID || schedule.route?.routeID || ''}&date=${selectedDate}&scheduleId=${schedule.scheduleID}`,
                              state: {
                                fromCityId: schedule.route?.fromCityID || schedule.route?.fromCityId,
                                toCityId: schedule.route?.toCityID || schedule.route?.toCityId,
                                routeId: schedule.routeID || schedule.route?.routeID,
                                travelDate: selectedDate,
                                from: schedule.route?.fromLocation || 'Unknown',
                                to: schedule.route?.toLocation || 'Unknown',
                                scheduleId: schedule.scheduleID
                              }
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                          >
                          Book Now
                          </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Information Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Schedule Information
            </h2>
            <p className="text-xl text-gray-600">
              Important details about our bus schedules
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Timing</h3>
              <p className="text-gray-600">
                All departure times are in Sri Lanka Standard Time (SLST - UTC+5:30). Please arrive at least 30 minutes before departure.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Daily Service</h3>
              <p className="text-gray-600">
                Most routes operate daily. Schedules may vary on holidays and special occasions.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bus className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Bus Types</h3>
              <p className="text-gray-600">
                We offer various bus types including Super Luxury, AC, and Non-AC buses.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Schedules;
